import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Ticket, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

// Poll every 2s, max 10 attempts (~20s)
const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!session_id) {
      setState({ status: "error", error: "Missing session id.", data: null });
      return;
    }
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setState({ status: "failed", data, error: `Payment ${data.payment_status}.` });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data, error: "Still confirming — check your email in a moment." });
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      } catch (err) {
        setState({
          status: "error",
          error: err?.response?.data?.detail || "Unable to confirm payment.",
          data: null,
        });
      }
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [session_id]);

  return (
    <div className="min-h-screen bg-black text-[#F5F5F0] flex items-center justify-center px-6 py-12" data-testid="payment-success-page">
      <div className="w-full max-w-lg">
        {state.status === "polling" && (
          <div className="text-center">
            <Loader2 size={28} className="animate-spin text-[#D9A6F5] mx-auto mb-6" />
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Confirming payment</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter">One moment…</h1>
            <p className="mt-4 text-[#A3A3A3]">We're just finalising your order with Stripe.</p>
          </div>
        )}

        {state.status === "paid" && (
          <div className="text-center">
            <CheckCircle2 size={48} className="text-[#D9A6F5] mx-auto mb-6" />
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Payment received</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[1]" data-testid="payment-success-title">
              {state.data?.kind === "membership" ? "Welcome, member." : "You're on the list."}
            </h1>
            <p className="mt-6 text-[#A3A3A3] leading-relaxed">
              We've sent your{" "}
              {state.data?.kind === "membership"
                ? "digital membership card"
                : `ticket${state.data?.ticket_ids?.length === 1 ? "" : "s"}`}
              {" "}to{" "}
              <span className="text-[#F5F5F0]">
                {state.data?.buyer_email || state.data?.buyer?.email}
              </span>
              .
              {state.data?.total_gbp != null && (
                <> Total paid: <span className="text-[#F5F5F0]">{fmtGBP(state.data.total_gbp)}</span>.</>
              )}
            </p>

            {state.data?.kind === "membership" && state.data?.membership_number && (
              <div
                data-testid="payment-success-membership-number"
                className="mt-8 border border-[#D9A6F5]/40 bg-[#D9A6F5]/5 p-5 text-center"
              >
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-2">
                  Your membership number
                </div>
                <div className="text-3xl font-black tracking-tight text-[#D9A6F5] font-mono">
                  {state.data.membership_number}
                </div>
              </div>
            )}

            {state.data?.ticket_ids?.length > 0 && (
              <div className="mt-10 space-y-3">
                {state.data.ticket_ids.map((tid, i) => (
                  <Link
                    key={tid}
                    to={`/t/${tid}`}
                    data-testid={`payment-success-ticket-${i}`}
                    className="w-full inline-flex items-center justify-between border border-white/15 hover:border-[#D9A6F5] px-5 py-4 transition-colors group"
                  >
                    <span className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em]">
                      <Ticket size={14} className="text-[#D9A6F5]" />
                      Open ticket {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#A3A3A3] group-hover:text-[#D9A6F5] transition-colors">→</span>
                  </Link>
                ))}
              </div>
            )}

            <div className="mt-10 pt-8 border-t border-white/10 text-xs uppercase tracking-[0.22em] text-[#A3A3A3] inline-flex items-center gap-2">
              <Mail size={12} className="text-[#D9A6F5]" /> Check spam if you can't see the email
            </div>
          </div>
        )}

        {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
              {state.status === "timeout" ? "Almost there" : "Something went wrong"}
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter">
              {state.status === "timeout"
                ? "We're still confirming."
                : state.status === "failed"
                ? "Payment didn't complete."
                : "We couldn't confirm your order."}
            </h1>
            <p className="mt-4 text-[#A3A3A3]">
              {state.error || "Please try again or contact us if this keeps happening."}
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
